// /invite and /recover: the pages B6's emails link to. They sit outside the workspace (no phone,
// event stream or workspace data), read their one-time fragment through the FragmentVault, and
// keep every secret in their flow object. Each ends in the normal sign-in, never a silent session.
import type { Session } from '@bcio-voice/contracts';
import { type FormEvent, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { APP_NAME } from '../config/brand.ts';
import type { ApiClient } from '../lib/api.ts';
import { Link, useRouter } from '../lib/router.tsx';
import { formatAbsolute } from '../lib/format.ts';
import type { SignOutNotice } from '../state/session.ts';
import { StatePanel } from '../ui/StatePanel.tsx';
import { roleText } from './contract.ts';
import type { Flow, FragmentSource } from './flow.ts';
import type { FragmentKind } from './fragment.ts';
import { InviteFlow, type InviteSnapshot } from './invite.ts';
import { RecoveryFlow, type RecoverySnapshot } from './recovery.ts';
import { AuthenticatorKey, CodeField, FieldError, FlowDiagnostics, IdentityFrame, NewCodeNotice, PasswordField } from './IdentityParts.tsx';

export interface FragmentFeed extends FragmentSource {
  subscribe(listener: () => void): () => void;
}

export interface IdentityPageProps {
  api: Pick<ApiClient, 'request'>;
  session: Session;
  fragments: FragmentFeed;
  onSignOut: () => void;
  onRequireFreshSignIn: (notice: SignOutNotice) => Promise<void>;
  onReloadSession: () => Promise<void>;
}

/** Wires a flow to the page: session first, then mount; a second link in this tab restarts it. */
function useFlow<S extends { phase: string }, F extends Flow<S> & { restart(token: string | null): void }>(flow: F, session: Session, fragments: FragmentFeed, kind: FragmentKind): S {
  const snap = useSyncExternalStore(flow.subscribe, flow.getSnapshot, flow.getSnapshot);
  const userId = session.user?.id ?? null;
  const csrf = session.csrf_token || null;
  useEffect(() => { flow.setSession(userId, csrf); }, [flow, userId, csrf]);
  useEffect(() => {
    flow.attach();
    return () => flow.detach();
  }, [flow]);
  useEffect(() => fragments.subscribe(() => {
    const held = fragments.take(kind);
    if (held) flow.restart(held.token);
  }), [flow, fragments, kind]);
  return snap;
}

/** Runs once when a flow reaches a state that requires a fresh sign-in. */
function useFreshSignIn(when: boolean, notice: SignOutNotice, require: (notice: SignOutNotice) => Promise<void>) {
  const done = useRef(false);
  useEffect(() => {
    if (!when || done.current) return;
    done.current = true;
    void require(notice);
  }, [when, notice, require]);
}

/** In the packaged desktop app these links belong in the web browser. */
export function OpenInBrowserNotice({ kind }: { kind: FragmentKind }) {
  return (
    <IdentityFrame title="Open this link in your web browser" section={kind === 'invite' ? 'Invitation' : 'Reset password'} foot={null}>
      <p className="field-hint">{kind === 'invite' ? 'Invitations' : 'Password resets'} are completed in your web browser, not in the {APP_NAME} desktop app. Open the link from your email in your browser.</p>
      <div className="form-actions"><Link to="/" className="btn btn-primary">Back to {APP_NAME}</Link></div>
    </IdentityFrame>
  );
}

// ---- Invitation ---------------------------------------------------------------------------------

export function InvitePage({ api, session, fragments, onSignOut, onRequireFreshSignIn, onReloadSession, flow: injected }: IdentityPageProps & { flow?: InviteFlow }) {
  const [flow] = useState(() => injected ?? new InviteFlow({ api, fragments, onSessionProblem: () => { void onReloadSession(); } }));
  const snap = useFlow<InviteSnapshot, InviteFlow>(flow, session, fragments, 'invite');
  const { navigate } = useRouter();
  useFreshSignIn(snap.phase === 'joined', snap.joined?.verified ? 'invite_joined_verified' : 'invite_joined', onRequireFreshSignIn);
  const title = inviteTitle(snap);
  return (
    <IdentityFrame title={title} section="Invitation">
      <InviteBody snap={snap} flow={flow} session={session} onSignOut={onSignOut} onSignIn={next => navigate(next, { replace: true })} onUnknown={() => { void onRequireFreshSignIn('fresh_sign_in').then(() => navigate('/', { replace: true })); }} />
    </IdentityFrame>
  );
}

export function inviteTitle(snap: InviteSnapshot): string {
  switch (snap.phase) {
    case 'details':
    case 'preparing':
      return snap.invite ? `Join ${snap.invite.tenant_name}` : 'Join a workspace';
    case 'verify':
    case 'accepting':
      return snap.mfa?.enrollment_required ? 'Set up two-step verification' : snap.mfa?.required ? 'Enter your verification code' : `Join ${snap.invite?.tenant_name ?? 'the workspace'}`;
    case 'joined':
      return `You joined ${snap.joined?.tenant ?? 'the workspace'}`;
    case 'no_link':
      return 'Open your invitation link';
    case 'unavailable':
      return 'Invitations aren’t available yet';
    case 'invalid':
      return 'This invitation can’t be used';
    case 'wrong_account':
      return 'This invitation is for a different account';
    case 'already_member':
      return 'You’re already in this workspace';
    case 'unknown':
      return 'We couldn’t confirm whether you joined';
    case 'account_changed':
      return 'The account in this browser changed';
    case 'cancelled':
      return 'Invitation not accepted';
    case 'error':
      return 'Couldn’t check this invitation';
    default:
      return 'Checking your invitation…';
  }
}

function InviteBody({ snap, flow, session, onSignOut, onSignIn, onUnknown }: { snap: InviteSnapshot; flow: InviteFlow; session: Session; onSignOut: () => void; onSignIn: (next: string) => void; onUnknown: () => void }) {
  switch (snap.phase) {
    case 'idle':
    case 'loading':
      return <StatePanel variant="loading" compact title="Checking your invitation…" />;
    case 'no_link':
      return (
        <div className="auth-form">
          <p className="field-hint">Open the invitation link from your email in this browser. For your security the link is read once and removed from the address bar, so reloading this page can’t reuse it.</p>
          <div className="form-actions"><Link to="/" className="btn">Go to sign in</Link></div>
        </div>
      );
    case 'unavailable':
      return (
        <>
          <StatePanel variant="unconfigured" compact title="This server isn’t set up for invitations yet" body="Nothing was changed. Ask the person who invited you to check back later." />
          <FlowDiagnostics diagnostic={snap.diagnostic} />
        </>
      );
    case 'invalid':
      return <p className="field-hint" role="alert">It may have expired, been revoked or already been used. Ask the person who invited you to send a new invitation.</p>;
    case 'wrong_account':
      return (
        <div className="auth-form">
          <p className="field-hint" role="alert">You’re signed in as {session.user?.email ?? 'another account'}. Sign out, then open the invitation link from your email again.</p>
          <FlowDiagnostics diagnostic={snap.diagnostic} />
          <div className="form-actions"><button type="button" className="btn btn-primary" onClick={onSignOut}>Sign out</button></div>
        </div>
      );
    case 'already_member':
      return (
        <div className="auth-form">
          <p className="field-hint">This account already belongs to that workspace, so the invitation wasn’t used. Sign in to continue.</p>
          <div className="form-actions"><Link to="/" className="btn btn-primary">Go to sign in</Link></div>
        </div>
      );
    case 'details':
    case 'preparing':
      return <InviteDetails snap={snap} flow={flow} session={session} />;
    case 'verify':
    case 'accepting':
      return <InviteVerify snap={snap} flow={flow} />;
    case 'joined':
      return (
        <div className="auth-form" role="status">
          {snap.joined?.verified ? <NewCodeNotice /> : null}
          <p>Your account is ready. Sign in to start using {snap.joined?.tenant ?? 'the workspace'}.</p>
          {snap.joined?.existingAccount ? (
            <p className="field-hint">If your account belongs to other workspaces too, {APP_NAME} may open one of those first. Choose {snap.joined.tenant} from the workspace list at the top of the page.</p>
          ) : null}
          {snap.joined?.fromDesktop ? (
            <p className="notice notice-info">This invitation came from {APP_NAME} desktop. After you sign in here, go back to the desktop app and choose Continue in your browser to start desktop sign-in again.</p>
          ) : null}
          <div className="form-actions"><button type="button" className="btn btn-primary" onClick={() => onSignIn(snap.joined?.next ?? '/')}>Sign in</button></div>
        </div>
      );
    case 'unknown':
      return (
        <div className="auth-form">
          <p className="field-hint" role="alert">The connection dropped before the server answered. Sign in to check: if the workspace is there, you’re in. If not, open the invitation link from your email again.</p>
          <FlowDiagnostics diagnostic={snap.diagnostic} />
          <div className="form-actions"><button type="button" className="btn btn-primary" onClick={onUnknown}>Sign in to check</button></div>
        </div>
      );
    case 'account_changed':
      return <p className="field-hint" role="alert">For your security the invitation was cleared from this page. Open the invitation link from your email again.</p>;
    case 'cancelled':
      return (
        <div className="auth-form">
          <p className="field-hint">Nothing changed. Open the link from your email again whenever you’re ready.</p>
          <div className="form-actions"><Link to="/" className="btn">Go to sign in</Link></div>
        </div>
      );
    default:
      return (
        <div className="auth-form">
          <p className="field-hint" role="alert">Check your connection, then try again.</p>
          <FlowDiagnostics diagnostic={snap.diagnostic} />
          <div className="form-actions"><button type="button" className="btn btn-primary" onClick={() => flow.retry()}>Try again</button></div>
        </div>
      );
  }
}

const NOTICE_COPY = {
  step_expired: 'That step timed out, so nothing was saved. Enter your password again to continue.',
  session_changed: 'Your browser session changed, so that step was cleared. Enter your password again to continue.',
  too_many_codes: 'Too many codes didn’t work, so that step was cleared. Enter your password again to get a new one.',
} as const;

function InviteDetails({ snap, flow, session }: { snap: InviteSnapshot; flow: InviteFlow; session: Session }) {
  const signedIn = !!session.user;
  const [mode, setMode] = useState<'new' | 'existing'>(signedIn ? 'existing' : 'new');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  useEffect(() => () => { setPassword(''); setConfirm(''); }, []);
  const invite = snap.invite;
  const busy = snap.phase === 'preparing';
  const existing = signedIn || mode === 'existing';
  if (!invite) return null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (!existing && password !== confirm) {
      setLocalError('The passwords don’t match.');
      return;
    }
    setLocalError(null);
    const value = password;
    // The password leaves this component's state as soon as it is sent.
    setPassword('');
    setConfirm('');
    void flow.prepare({ password: value, displayName: existing ? null : name });
  };
  const error = localError ?? snap.fieldError;

  return (
    <form className="auth-form" onSubmit={submit} noValidate aria-describedby={error ? 'invite-error' : undefined} aria-busy={busy || undefined}>
      <dl className="auth-facts">
        <div><dt>Workspace</dt><dd><strong>{invite.tenant_name}</strong></dd></div>
        <div><dt>Invited</dt><dd>{invite.email}</dd></div>
        <div><dt>Role</dt><dd>{roleText(invite.role_ids)}</dd></div>
        <div><dt>Link expires</dt><dd>{formatAbsolute(invite.expires_at)}</dd></div>
      </dl>
      {invite.continue_path?.startsWith('/desktop/authorize?') ? (
        <p className="notice notice-info">You opened this from {APP_NAME} desktop. After you join, sign in here, then start desktop sign-in again from the app.</p>
      ) : null}
      {snap.notice ? <p className="notice notice-warn" role="status">{NOTICE_COPY[snap.notice]}</p> : null}
      {signedIn ? (
        <p className="field-hint">You’re signed in as {session.user?.email}. Confirm it’s you with your current password.</p>
      ) : (
        <div role="tablist" aria-label="Do you have an account?" className="segmented">
          <button type="button" role="tab" className="segment" aria-selected={mode === 'new'} onClick={() => { setMode('new'); setLocalError(null); }} disabled={busy}>I’m new to {APP_NAME}</button>
          <button type="button" role="tab" className="segment" aria-selected={mode === 'existing'} onClick={() => { setMode('existing'); setLocalError(null); }} disabled={busy}>I have an account</button>
        </div>
      )}
      {existing ? (
        <PasswordField id="invite-password" label="Your current password" value={password} onChange={setPassword} autoComplete="current-password" hint={`The password you use to sign in to ${APP_NAME} as ${invite.email}.`} />
      ) : (
        <>
          <div className="field">
            <label htmlFor="invite-name">Your name</label>
            <input id="invite-name" autoComplete="name" maxLength={120} value={name} onChange={e => setName(e.target.value)} />
          </div>
          <PasswordField id="invite-password" label="Choose a password" value={password} onChange={setPassword} autoComplete="new-password" hint="At least 12 characters." />
          <PasswordField id="invite-confirm" label="Type it again" value={confirm} onChange={setConfirm} autoComplete="new-password" />
        </>
      )}
      <FieldError id="invite-error" message={error} />
      <div className="form-actions">
        <button type="button" className="btn" onClick={() => flow.discard()} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>{busy ? 'Checking…' : 'Continue'}</button>
      </div>
    </form>
  );
}

function InviteVerify({ snap, flow }: { snap: InviteSnapshot; flow: InviteFlow }) {
  const [code, setCode] = useState('');
  const busy = snap.phase === 'accepting';
  const mfa = snap.mfa;
  useEffect(() => () => setCode(''), []);
  if (!mfa) return null;
  const tenant = snap.invite?.tenant_name ?? 'the workspace';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const value = code;
    setCode('');
    void flow.accept(mfa.required ? value : null);
  };
  return (
    <form className="auth-form" onSubmit={submit} noValidate aria-describedby={snap.fieldError ? 'verify-error' : undefined} aria-busy={busy || undefined}>
      {mfa.enrollment_required ? (
        <>
          <p className="field-hint">Your role in {tenant} needs two-step verification. Add {APP_NAME} to an authenticator app, then enter the code it shows. It isn’t turned on until that code is checked.</p>
          <AuthenticatorKey mfa={mfa} />
        </>
      ) : mfa.required ? (
        <p className="field-hint">Enter the 6-digit code from the authenticator app you already use for {APP_NAME}.</p>
      ) : (
        <p className="field-hint">Everything is ready. Join {tenant} to finish.</p>
      )}
      {mfa.required ? <CodeField id="invite-code" value={code} onChange={setCode} error={!!snap.fieldError} autoFocus /> : null}
      {snap.stepExpiresAt ? <p className="meta">This step stays open until {formatAbsolute(snap.stepExpiresAt)}.</p> : null}
      <p className="meta">
        {mfa.required ? 'When you join, you’ll sign in again with your password and the next code your app shows. Each code works only once.' : 'When you join, you’ll sign in again.'}
        {mfa.enrollment_required ? ' If you were already signed in on other devices, setting up two-step verification signs you out there too, including any call in progress.' : null}
      </p>
      <FieldError id="verify-error" message={snap.fieldError} />
      <div className="form-actions">
        <button type="button" className="btn" onClick={() => flow.back()} disabled={busy}>Back</button>
        <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>{busy ? 'Joining…' : `Join ${tenant}`}</button>
      </div>
    </form>
  );
}

// ---- Password recovery --------------------------------------------------------------------------

export function RecoverPage({ api, session, fragments, onRequireFreshSignIn, onReloadSession, flow: injected }: IdentityPageProps & { flow?: RecoveryFlow }) {
  const [flow] = useState(() => injected ?? new RecoveryFlow({ api, fragments, onSessionProblem: () => { void onReloadSession(); } }));
  const snap = useFlow<RecoverySnapshot, RecoveryFlow>(flow, session, fragments, 'recover');
  const { navigate } = useRouter();
  useFreshSignIn(snap.phase === 'done', snap.verified ? 'password_reset_verified' : 'password_reset', onRequireFreshSignIn);
  return (
    <IdentityFrame title={recoverTitle(snap)} section="Reset password">
      <RecoverBody snap={snap} flow={flow} onSignIn={() => navigate('/', { replace: true })} onUnknown={() => { void onRequireFreshSignIn('fresh_sign_in').then(() => navigate('/', { replace: true })); }} />
    </IdentityFrame>
  );
}

export function recoverTitle(snap: RecoverySnapshot): string {
  switch (snap.phase) {
    case 'request':
    case 'requesting':
      return 'Reset your password';
    case 'requested':
      return 'Check your email';
    case 'reset':
    case 'resetting':
      return 'Choose a new password';
    case 'done':
      return 'Password changed';
    case 'unavailable':
      return 'Password reset isn’t available';
    case 'invalid':
      return 'This reset link can’t be used';
    case 'unknown':
      return 'We couldn’t confirm the change';
    case 'account_changed':
      return 'The account in this browser changed';
    case 'error':
      return 'Couldn’t load password reset';
    default:
      return 'Reset your password';
  }
}

function RecoverBody({ snap, flow, onSignIn, onUnknown }: { snap: RecoverySnapshot; flow: RecoveryFlow; onSignIn: () => void; onUnknown: () => void }) {
  switch (snap.phase) {
    case 'idle':
    case 'loading':
      return <StatePanel variant="loading" compact title="Loading…" />;
    case 'unavailable':
      return (
        <>
          <StatePanel variant="unconfigured" compact title="This server can’t send password reset emails yet" body="Ask an administrator to help you get back in." />
          <FlowDiagnostics diagnostic={snap.diagnostic} />
          <div className="form-actions"><Link to="/" className="btn">Back to sign in</Link></div>
        </>
      );
    case 'request':
    case 'requesting':
      return <RecoverRequest snap={snap} flow={flow} />;
    case 'requested':
      return (
        <div className="auth-form" role="status">
          <p>If that address belongs to an account that can reset its password, a link is on its way. It works once.</p>
          <p className="field-hint">Nothing arrived? Check your spam folder, or ask an administrator.</p>
          <div className="form-actions"><Link to="/" className="btn">Back to sign in</Link></div>
        </div>
      );
    case 'reset':
    case 'resetting':
      return <RecoverReset snap={snap} flow={flow} />;
    case 'done':
      return (
        <div className="auth-form" role="status">
          {snap.verified ? <NewCodeNotice /> : null}
          <p>You’ve been signed out everywhere. Sign in with your new password.</p>
          <div className="form-actions"><button type="button" className="btn btn-primary" onClick={onSignIn}>Sign in</button></div>
        </div>
      );
    case 'invalid':
      return (
        <div className="auth-form">
          <p className="field-hint" role="alert">It may have expired, already been used, or had too many failed tries. Request a new link.</p>
          <FlowDiagnostics diagnostic={snap.diagnostic} />
          <div className="form-actions"><button type="button" className="btn btn-primary" onClick={() => flow.startOver()}>Request a new link</button></div>
        </div>
      );
    case 'unknown':
      return (
        <div className="auth-form">
          <p className="field-hint" role="alert">The connection dropped before the server answered. Try signing in with your new password; if that doesn’t work, request a new link.</p>
          <FlowDiagnostics diagnostic={snap.diagnostic} />
          <div className="form-actions">
            <button type="button" className="btn" onClick={() => flow.startOver()}>Request a new link</button>
            <button type="button" className="btn btn-primary" onClick={onUnknown}>Sign in</button>
          </div>
        </div>
      );
    case 'account_changed':
      return <p className="field-hint" role="alert">For your security the reset link was cleared from this page. Open it from your email again, or request a new one.</p>;
    default:
      return (
        <div className="auth-form">
          <p className="field-hint" role="alert">Check your connection, then try again.</p>
          <FlowDiagnostics diagnostic={snap.diagnostic} />
          <div className="form-actions"><button type="button" className="btn btn-primary" onClick={() => flow.retry()}>Try again</button></div>
        </div>
      );
  }
}

function RecoverRequest({ snap, flow }: { snap: RecoverySnapshot; flow: RecoveryFlow }) {
  const [email, setEmail] = useState('');
  const busy = snap.phase === 'requesting';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!busy) void flow.request(email);
  };
  return (
    <form className="auth-form" onSubmit={submit} noValidate aria-describedby={snap.fieldError ? 'recover-error' : undefined}>
      <p className="field-hint">Enter the email you sign in with. If it belongs to an account that can reset its password, we’ll email a link.</p>
      <div className="field">
        <label htmlFor="recover-email">Work email</label>
        <input id="recover-email" type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} />
      </div>
      <FieldError id="recover-error" message={snap.fieldError} />
      <div className="form-actions">
        <Link to="/" className="btn">Back to sign in</Link>
        <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>{busy ? 'Sending…' : 'Send reset link'}</button>
      </div>
    </form>
  );
}

function RecoverReset({ snap, flow }: { snap: RecoverySnapshot; flow: RecoveryFlow }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const busy = snap.phase === 'resetting';
  useEffect(() => () => { setPassword(''); setConfirm(''); setCode(''); }, []);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (password !== confirm) {
      setLocalError('The passwords don’t match.');
      return;
    }
    setLocalError(null);
    const [p, c] = [password, code];
    setPassword('');
    setConfirm('');
    setCode('');
    void flow.reset(p, c);
  };
  const error = localError ?? snap.fieldError;
  return (
    <form className="auth-form" onSubmit={submit} noValidate aria-describedby={error ? 'reset-error' : undefined} aria-busy={busy || undefined}>
      <PasswordField id="reset-password" label="New password" value={password} onChange={setPassword} autoComplete="new-password" hint="At least 12 characters." autoFocus />
      <PasswordField id="reset-confirm" label="Type it again" value={confirm} onChange={setConfirm} autoComplete="new-password" />
      <CodeField id="reset-code" value={code} onChange={setCode} optional error={snap.codeHint} hint="Needed if you sign in with an authenticator app (owners, administrators and supervisors always do). Leave it empty otherwise." />
      <p className="meta">Changing your password signs you out everywhere, including any call in progress.</p>
      <FieldError id="reset-error" message={error} />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>{busy ? 'Saving…' : 'Change password'}</button>
      </div>
    </form>
  );
}
